import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { GridOptions } from "ag-grid-community";
import moment from 'moment';
import { Subscription } from 'rxjs';
import { ManualAwardsService } from 'src/app/gamification/command-center/services/manual-awards.service';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { BareMetricsCalendarPreset } from 'src/app/reusables/baremetrics-calendar/baremetrics-calendar.component';
import { CvCustomDialogService } from 'src/app/reusables/custom-dialog/cv-custom-dialog.service';
import { FilterTextPipe } from 'src/app/reusables/pipes/filter-text.pipe';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
  selector: 'cv-awards-history-list',
  templateUrl: './awards-history-list.component.html',
  styleUrls: ['./awards-history-list.component.scss']
})

export class AwardsHistoryListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isVisible: Boolean = false;
  @Output() closeAwardHistoryPopup = new EventEmitter<void>();
  @ViewChild('awardsHistoryListGrid') agGrid: AgGridAngular;

  gf = null;
  mas = null;
  dateFormat = CVAngularDateFormat;
  filteredHistory: string[] = null;
  presets: BareMetricsCalendarPreset[];
  gridOptions: GridOptions = null;
  _data: any = null;
  historyPopup: any;
  loading: boolean = false;
  filterText: string = "";
  startDate: Date = new Date();
  endDate: Date = new Date();
  latestDate: Date = new Date();
  rowData = [];
  hoveredRowIndex: number = null;
  idCounter = 0;

  modalOptions: NgbModalOptions = {
    keyboard: false,
    backdrop: 'static',
    centered: true,
  };
  columnLocalization = new Map();

  private _sidebarSub: Subscription;

  constructor(
    private AgGridHelperService: AgGridHelperService,
    private manualAwardsService: ManualAwardsService,
    private cvLocalizationService: CVLocalizationService,
    private datePipe: DatePipe,
    private dialogService: CvCustomDialogService,
    private navigationService: NavigationService
  ) {
    this.mas = this.manualAwardsService.data;
    this.filterText = ""
  }

  ngOnInit(): void {
    this.loading = true;
    this.loadLocalization();
    this.loadServiceApis();
    this.setupGrid();
    this.gridOptions.rowData.forEach((rowData) => {
      rowData.hidden = false;
    });

      // Listen for sidebar changes and toggle the overlay to use NavigationService
    this._sidebarSub = this.navigationService.sidebarActiveSubject.subscribe(() => {
      
    });
  }

  ngOnDestroy(): void {
  this._sidebarSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isVisible && changes.isVisible.currentValue === true) {
      this.navigationService.showOverlay = true;
      this.loadData();
    }
    if (changes.isVisible && changes.isVisible.currentValue === false) {
      this.navigationService.showOverlay = false;
    }
  }

  async loadLocalization() {
    await this.cvLocalizationService.GetSection("Manual_Awards").then((map) => {
      this.columnLocalization = map;
      this.loadPresets();
      this.setupGrid();
    });
  }

  async loadServiceApis() {
    try {
      this._data = await this.manualAwardsService.loadAllAwardHistory(this.startDate.getTime(), this.endDate.getTime());
    }
    catch {
      this.loading = false;
    }
    this.loadData();
  }

  cleanData() {
    this._data = []
    this.agGrid?.api?.setGridOption('rowData', []);
  }

  loadData() {
    this.loading = true;

    this.manualAwardsService.loadAllAwardHistory(
      this.startDate.getTime(),
      this.endDate.getTime(),
    )
      .then((result: any) => {
        if (result.FullHistoryItems.length > 0) {
          if (this._data.length > 0)
            this.cleanData();

          result.FullHistoryItems.forEach((item, index) => {
            item._id = index;
          });

          this.gridOptions.getRowId = (params) => params.data._id.toString();

          this._data = result.FullHistoryItems
          if (this.agGrid?.api) {
            this.agGrid?.api?.setGridOption('rowData', result.FullHistoryItems);
          }
        } else {
          this.cleanData();
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  rowClassRules = {
    'hovered-row': params => params.node.isHovered
  };

  onRowMouseOut = (params) => {
    params.node.isHovered = false;
  };

  onRowMouseOver = (params) => {
    this.hoveredRowIndex = params.node.rowIndex;
    params.api.refreshCells({ force: true });
  };

  getColumnName(key: string, defaultValue: string = key): string {
    let columnNameLocalization = this.columnLocalization.get(key);
    if (columnNameLocalization && columnNameLocalization.length > 0)
      return columnNameLocalization;
    else
      return defaultValue;
  }

  async removeAward(rowParams) {
    let title = await this.cvLocalizationService.GetLocalText("Manual_Awards_Validation", "Confirm_Remove_Award", "Confirm Remove Award");
    let msg = await this.cvLocalizationService.GetLocalText("Manual_Awards_Validation", "Confirm_Remove_Award_Message", "Are you sure you want to remove this award? This action can't be undone.");
    let okLabel = this.cvLocalizationService.getCachedText("Common", "Ok", "Ok");
    let cancelLabel = this.cvLocalizationService.getCachedText("Common", "Cancel_Button", "Cancel");

    this.dialogService.showDialog(title, msg, 4, cancelLabel, okLabel, true, () => {
      this.manualAwardsService.removeManualAward(rowParams.data)
        .then((result: any) => {
          if (result) {
            this.agGrid?.api?.applyTransaction({ remove: [rowParams.data] });
          }
        });
    }, () => { }, 'modal-alert', true, this.modalOptions);
  }

  setupGrid() {
    this.gridOptions = this.AgGridHelperService.createGridOptions({
      defaultColDef: {
        sortable: false
      },
      rowClassRules: {
        'award-icon': (params) => params.node.rowIndex === this.hoveredRowIndex
      },
      onCellMouseOver: (event) => {
        const newHoveredRowIndex: string = event.node ? event.node.rowIndex.toString() : '';
        this.hoveredRowIndex = Number(newHoveredRowIndex);
        const hoveredRowElement = document.querySelector(`.ag-row[row-index="${newHoveredRowIndex}"]`);
        const awardIcon = hoveredRowElement?.querySelector('.award-icon') as HTMLElement;
        if (awardIcon) {
          awardIcon.style.display = 'block';
        }
      },
      onCellMouseOut: (event) => {
        const rowIndex = event.node.rowIndex;
        const rowElement = document.querySelector(`.ag-row[row-index="${rowIndex}"]`);
        const awardIcon = rowElement?.querySelector('.award-icon') as HTMLElement;
        if (awardIcon) {
          awardIcon.style.display = 'none';
        }
      },
      columnDefs: [
        {
          field: 'Name',
          headerName: this.getColumnName('Badge_Name', 'Badge Name'),
          suppressMovable: true,
          suppressMenu: true,
          cellRenderer: function (params) {
            const badge = '<img class="mr-3" src="../Admin/ImageResizer.aspx?imgid=' + params.data['BadgeID'] + '&w=40&h=40" width="40" height="40"/>';
            const name = '<div>' + params.data['Name'] + '</div>';
            const coin = '<div><span class="maHistoryCoinIcon"></span><span>' + params.data['CoinAmt'] + '</span></div>';
            return '<div class="d-flex align-items-center h-100">' + badge + '<div style="line-height: 1.5em">' + name + coin + '</div></div>';
          },
        },
        {
          field: 'RecipientName',
          headerName: this.getColumnName('Recipient_Name', 'Recipient'),
          suppressMovable: true,
          suppressMenu: true,
          cellRenderer: function (params) {
            const img =
              '<div class="round mr-3"><img src="../Admin/ImageResizer.aspx?imgid=' + params.data['RecipientImageID'] + '&w=40&h=40" width="40" height="40"/></div>';
            const name = '<div>' + params.data['RecipientName'] + '</div>';
            const role = '<div>' + params.data['RecipientRole'] + '</div>';
            return '<div class="d-flex align-items-center h-100">' + img + '<div style="line-height: 1.5em">' + name + role + '</div></div>';
          },
        },
        {
          field: 'Date',
          headerName: this.getColumnName('Column_Date', 'Date'),
          suppressMovable: true,
          suppressMenu: true,
          comparator: this.compareDate,
          sort: 'desc',
          cellRenderer: (params) => {
            return '<div class="d-flex align-items-center h-100">' + this.datePipe.transform(params.data['Date'], CVAngularDateTimeFormat) + '</div>';
          }
        },
        {
          field: 'AwarderName',
          headerName: this.getColumnName('Awarded_By', 'Awarded By'),
          suppressMovable: true,
          suppressMenu: true,
          cellRenderer: (params) => {
            const rowId = params.node.id;
            const iconId = `removeButton_${rowId}`;
            const name = '<div style="flex: 1;">' + params.data['AwarderName'] + '</div>';
            const role = '<div style="flex: 1; margin-top: -5px;">' + params.data['AwarderRole'] + '</div>';
            const icon = `
              <div style="margin-right:54px; display:none" class="award-icon"> 
                  <button type="button" id="${iconId}" class="remove-award" style="border: none; background-color: transparent;" >
                      <svg width="18" height="18" style="fill: #C1C1C1;">
                          <use xlink:href="../App_Themes/clearview-default/icons/utility/delete.svg#delete"></use>
                      </svg>
                  </button>
              </div>`;

            const showIcon = (event) => {
              const element = event.currentTarget;
              const awardIcon = element.querySelector('.award-icon');
              if (awardIcon) {
                awardIcon.style.display = 'block';
              }
            };
            (window as any).showIcon = showIcon;

            params.eGridCell.addEventListener('click', () => this.removeAward(params));
            return `
                <div style="display: flex;">
                    <div style="flex: 1;">
                        ${name}
                        ${role}
                    </div>
                    <div style="display: flex; align-items: center;" onmouseover="showIcon(event)">
                        ${icon}
                    </div>
                </div>
            `;
          }
        }
      ],
      rowHeight: 50,
      alwaysShowVerticalScroll: true,
      suppressRowClickSelection: true,
      suppressCellFocus: true,
      onGridReady: (params) => {
        this.loadData();
        params.api.sizeColumnsToFit();
        params.api.forEachNode(node => {
          const rowNode = params.api.getRowNode(node.id);
          if (rowNode) {
            const rowElement = document.querySelector(`[row-id="${rowNode.id}"]`) as HTMLElement;
            if (rowElement) {
              const awardIcon = rowElement.querySelector('.award-icon') as HTMLElement;
              if (awardIcon) {
                awardIcon.style.display = 'none';
              }
            }
          }
        });
      },
      onGridSizeChanged: (e) => {
        e.api.sizeColumnsToFit();
      },
      localeText: { noRowsToShow: this.getColumnName('No_History', 'No History') },
      rowData: [],
      isExternalFilterPresent: () => this.filterText.length > 0,
      doesExternalFilterPass: (node) => {
        const searchText = this.filterText.toLowerCase();
        const columnsToSearch = ['Name', 'RecipientName', 'AwarderName'];
        for (let column of columnsToSearch) {
          const cellValue = node.data[column] ? node.data[column].toString().toLowerCase() : '';
          if (cellValue.includes(searchText)) {
            return true;
          }
        }
        return false;
      }
    });
  }

  handleDateChanged(detail: { start_date: string, end_date: string }) {
    const startDate = new Date(detail.start_date);
    const endDate = new Date(detail.end_date);
    this.startDate = startDate;
    this.endDate = endDate;
    this.loadData();
  };

  async loadPresets() {
    this.presets = [
      {
        name: 'Today_Range',
        label: await this.cvLocalizationService.GetLocalText("Manual_Awards", "Today_Range", "Today"),
        start: moment(),
        end: moment()
      },
      {
        name: 'Yesterday_range',
        label: await this.cvLocalizationService.GetLocalText("Manual_Awards", "Yesterday_Range", "Yesterday"),
        start: moment().subtract(1, "days"),
        end: moment().subtract(1, "days")
      },
      {
        name: 'ThisWeek_range',
        label: await this.cvLocalizationService.GetLocalText("Manual_Awards", "This_Week_Range", "This Week"),
        start: moment().startOf("weeks"),
        end: moment()
      },
      {
        name: 'LasWeek_range',
        label: await this.cvLocalizationService.GetLocalText("Manual_Awards", "Last_Week_Range", "Last Week"),
        start: moment().subtract(1, "weeks").startOf("weeks"),
        end: moment().subtract(1, "weeks").endOf("weeks")
      },
      {
        name: 'ThisMonth_range',
        label: await this.cvLocalizationService.GetLocalText("Manual_Awards", "This_Month_Range", "This Month"),
        start: moment().startOf("months"),
        end: moment()
      },
      {
        name: 'LastMonth_range',
        label: await this.cvLocalizationService.GetLocalText("Manual_Awards", "Last_Month_Range", "Last Month"),
        start: moment().subtract(1, "months").startOf("months"),
        end: moment().subtract(1, "months").endOf("months")
      },
      {
        name: 'ThisQuarter_range',
        label: await this.cvLocalizationService.GetLocalText("Manual_Awards", "This_Quarter_Range", "This Quarter"),
        start: moment().quarter(moment().quarter()).startOf('quarter'),
        end: moment().quarter(moment().quarter()).endOf('quarter')
      },
      {
        name: 'LastQuarter_range',
        label: await this.cvLocalizationService.GetLocalText("Manual_Awards", "Last_Quarter_Range", "Last Quarter"),
        start: moment().quarter(moment().quarter() - 1).startOf('quarter'),
        end: moment().quarter(moment().quarter() - 1).endOf('quarter')
      }
    ];
  };

  updateGridFilter() {
    if (this.gridOptions.api) {
      this.agGrid?.api?.setQuickFilter(this.filterText);
    }
  };

  clearSearch() {
    this.filterText = "";
    this.filteredHistory = new FilterTextPipe().transform(this._data, this.filterText);
    this.agGrid?.api?.setGridOption('rowData', this.filteredHistory);
    this.updateGridFilter();
  };

  show() {
    this.loadData();
    this.loadPresets();
  };

  hide() {
    this.navigationService.showOverlay = false;
    this.closeAwardHistoryPopup.emit();
  };

  filterAwardHistory() {
    if (this._data !== null) {
      this.filteredHistory = new FilterTextPipe().transform(this._data, this.filterText);
      this.agGrid?.api?.setGridOption('rowData', this.filteredHistory);
      this.agGrid?.api?.onFilterChanged();
    }

  }

  compareDate(date1: Date, date2: Date) {
    return new Date(date1).compareTo(new Date(date2));
  }
}
